import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "../api/apiClient";
import { ENDPOINTS } from "../api/endpoints";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search } from "lucide-react";

import ServiceList from "../components/services/ServiceList";
import ServiceForm from "../components/services/ServiceForm";
import ConfirmationModal from "@/components/ui/ConfirmationModal";
import { transformServiceToBackend } from "../services/transformers";

// Local transformServiceToFrontend - converts backend camelCase to frontend snake_case for forms
const transformServiceToFrontend = (backendService) => {
  return {
    id: backendService.id,
    service_name: backendService.name,
    service_code: backendService.serviceCode || "",
    category: backendService.category || "general",
    subcategory: backendService.subcategory || "",
    description: backendService.description || "",
    duration_minutes: backendService.totalDurationMinutes || 30,
    preparation_time: backendService.preparationTime || 0,
    base_price: backendService.basePrice || 0,
    equipment_required: backendService.equipmentRequired || [],
    supplies_required: backendService.suppliesRequired || [],
    contraindications: backendService.contraindications || [],
    side_effects: backendService.sideEffects || [],
    pre_service_requirements: backendService.preServiceRequirements || [],
    post_service_care: backendService.postServiceCare || [],
    frequency_recommendations: backendService.frequencyRecommendations || "",
    requires_doctor: backendService.requiresDoctor || false,
    requires_room: backendService.requiresRoom || false,
    requires_lab_work: backendService.requiresLabWork || false,
    consent_form_required: backendService.consentFormRequired || false,
    status: backendService.status || "active",
    notes: backendService.notes || "",
    created_date: backendService.createdAt,
    created_by: backendService.createdBy || "",
    updated_date: backendService.updatedAt,
    rooms_required: backendService.roomsRequired || [],
    doctors_assigned: backendService.doctors_assigned || [], // Add the missing doctors_assigned field
    equipment_by_room: backendService.equipmentByRoom || {},
  };
};

export default function Services() {
  const [showForm, setShowForm] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [error, setError] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);

  const queryClient = useQueryClient();

  // Fetch services using React Query
  const {
    data: backendServices = [],
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: ["services"],
    queryFn: () => apiClient.get(ENDPOINTS.SERVICES.LIST),
    staleTime: 5 * 60 * 1000, // 5 minutes
    onError: (error) => {
      console.error("Error loading services:", error);
      setError(error.message || "Failed to load services");
    },
  });

  // Transform backend services to frontend format
  const services = backendServices.map(transformServiceToFrontend);

  // Mutations for service operations
  const createServiceMutation = useMutation({
    mutationFn: (serviceData) =>
      apiClient.post(ENDPOINTS.SERVICES.CREATE, serviceData),
    onSuccess: () => {
      queryClient.invalidateQueries(["services"]);
      setShowForm(false);
      setEditingService(null);
      setError(null);
    },
    onError: (error) => {
      console.error("Error creating service:", error);
      setError(error.message || "Failed to create service");
    },
  });

  const updateServiceMutation = useMutation({
    mutationFn: ({ id, data }) =>
      apiClient.put(ENDPOINTS.SERVICES.UPDATE(id), data),
    onSuccess: () => {
      queryClient.invalidateQueries(["services"]);
      setShowForm(false);
      setEditingService(null);
      setError(null);
    },
    onError: (error) => {
      console.error("Error updating service:", error);
      setError(error.message || "Failed to update service");
    },
  });

  const deleteServiceMutation = useMutation({
    mutationFn: (serviceId) =>
      apiClient.delete(ENDPOINTS.SERVICES.DELETE(serviceId)),
    onSuccess: () => {
      queryClient.invalidateQueries(["services"]);
      setError(null);
    },
    onError: (error) => {
      console.error("Error deleting service:", error);
      setError(error.message || "Failed to delete service");
    },
  });

  const handleAddService = () => {
    setEditingService(null);
    setShowForm(true);
  };

  const handleEditService = (service) => {
    setEditingService(service);
    setShowForm(true);
  };

  const handleSaveService = (data) => {
    const backendData = transformServiceToBackend(data);

    if (editingService) {
      updateServiceMutation.mutate({
        id: editingService.id,
        data: backendData,
      });
    } else {
      createServiceMutation.mutate(backendData);
    }
  };

  const handleDeleteService = (serviceId) => {
    console.log('[DEBUG] handleDeleteService called with serviceId:', serviceId);
    console.log('[DEBUG] All services:', services.map(s => ({ id: s.id, name: s.service_name })));
    setServiceToDelete(serviceId);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (serviceToDelete) {
      console.log('[DEBUG] handleConfirmDelete - deleting service ID:', serviceToDelete);
      const serviceToDeleteData = services.find(s => s.id === serviceToDelete);
      console.log('[DEBUG] Service to delete:', serviceToDeleteData);
      deleteServiceMutation.mutate(serviceToDelete);
      setServiceToDelete(null);
    }
    setDeleteConfirmOpen(false);
  };

  const filteredServices = services.filter((service) => {
    const searchMatch =
      service.service_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (service.description &&
        service.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const categoryMatch =
      categoryFilter === "all" || service.category === categoryFilter;
    return searchMatch && categoryMatch;
  });

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            Manage Services
          </h1>
          <p className="text-slate-600">
            Create, edit, and manage all services offered by the clinic.
          </p>
        </div>
        <Button
          onClick={handleAddService}
          className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg"
          data-testid="create-service-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Service
        </Button>
      </div>

      {/* Error Display */}
      {(error || queryError) && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">
            {error || queryError?.message}
          </span>
          <button
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
            onClick={() => setError(null)}
          >
            <svg
              className="fill-current h-6 w-6 text-red-500"
              role="button"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
            >
              <title>Close</title>
              <path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z" />
            </svg>
          </button>
        </div>
      )}

      {/* Search and Filter */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
          <Input
            placeholder="Search by service name or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-white/60 backdrop-blur-sm border-slate-200/60"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-3 py-2 border border-slate-200/60 rounded-md bg-white/60 backdrop-blur-sm focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Categories</option>
          <option value="primary_care">Primary Care</option>
          <option value="preventive_care">Preventive Care</option>
          <option value="diagnostic_services">Diagnostic Services</option>
          <option value="specialist_consultations">
            Specialist Consultations
          </option>
          <option value="womens_health">Women&apos;s Health</option>
          <option value="mens_health">Men&apos;s Health</option>
          <option value="pediatric_services">Pediatric Services</option>
          <option value="mental_health">Mental Health</option>
          <option value="minor_procedures">Minor Procedures</option>
          <option value="rehabilitation_services">
            Rehabilitation Services
          </option>
          <option value="nutritional_services">Nutritional Services</option>
          <option value="chronic_disease_management">
            Chronic Disease Management
          </option>
        </select>
      </div>

      {/* Services List */}
      <ServiceList
        services={filteredServices}
        isLoading={isLoading}
        onEditService={handleEditService}
        onDeleteService={handleDeleteService}
      />

      {/* Forms */}
      {showForm && (
        <ServiceForm
          service={editingService}
          onSave={handleSaveService}
          onClose={() => {
            setShowForm(false);
            setEditingService(null);
          }}
          isLoading={
            createServiceMutation.isPending || updateServiceMutation.isPending
          }
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        onConfirm={handleConfirmDelete}
        title="Delete Service"
        message="Are you sure you want to delete this service? Deleting a service may affect historical appointment records."
        confirmText="Delete"
        cancelText="Cancel"
        variant="destructive"
      />
    </div>
  );
}
